/* tslint:disable:max-file-line-count */

import * as React from 'react';

import { ILocalization, LocalizedText } from 'bernie-l10n';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { PropertyInfoPolicyViewModel, PropertyInfoSectionViewModel, PropertyInfoSubSectionViewModel } from 'models';

import { UitkHeading } from 'uitk-react-text';

import { LazyImage } from 'components/common/lazy/lazy-image';
import { NavigationRefs } from 'components/infosite/navigation/navigation-refs.tsx';
import { PropertyPoliciesSection } from './/property-policies-section';
import { renderAmenityBody } from './render-amenity-helper';

interface PropertyPoliciesProps {
  l10n: ILocalization;
  policies?: PropertyInfoPolicyViewModel;
  petsPolicy?: PropertyInfoSubSectionViewModel;
  renovationsAndClosuresSection?: PropertyInfoSectionViewModel;
  usesGrid?: boolean;
}

export const PropertyPolicies: React.FunctionComponent<PropertyPoliciesProps> = ({
  l10n,
  petsPolicy,
  policies,
  renovationsAndClosuresSection
}) => {
  if (!policies) {
    return null;
  }

  return (
    <Viewport>
      <ViewSmall>
        <PropertyPoliciesContent
          policies={policies}
          petsPolicy={petsPolicy}
          renovationsAndClosuresSection={renovationsAndClosuresSection}
          l10n={l10n}
          usesGrid={false}
        />
      </ViewSmall>
      <ViewLarge>
        <PropertyPoliciesContent
          policies={policies}
          petsPolicy={petsPolicy}
          renovationsAndClosuresSection={renovationsAndClosuresSection}
          l10n={l10n}
          usesGrid={true}
        />
      </ViewLarge>
    </Viewport>
  );
};

export const PropertyPoliciesContent = ({ policies, petsPolicy, renovationsAndClosuresSection, l10n, usesGrid }: PropertyPoliciesProps) => {
  const info = policies && policies.info;
  const paymentOptions = info && info.paymentOptions;
  const childBeds = info && info.childBeds;
  const checkinInstructions = info && info.checkinInstructions;
  const checkinPolicies = info && info.checkinPolicies;
  const checkoutPolicies = info && info.checkoutPolicies;
  const accessMethodPolicies = info && info.accessMethodPolicies;
  const secondaryTitleClassNames = 'policies__header--secondary uitk-type-heading-500 uitk-type-bold';
  const gridClass = usesGrid && 'uitk-grid';
  const cellClass = usesGrid && 'uitk-cell';

  return (
    <div
      className={`property-policies ${gridClass} all-y-padding-three l-x-padding-six xl-x-padding-six s-x-padding-three m-x-padding-three`}
      ref={NavigationRefs.Policies}
    >
      <div className={`${cellClass} l-cell-1-3 xl-cell-1-3 all-y-padding-three`}>
        <UitkHeading level={2} size={600}>
          {policies.title}
        </UitkHeading>
      </div>
      <div className={`${cellClass} l-cell-2-3 xl-cell-2-3`}>
        <CheckInCheckOut
          checkinPolicies={checkinPolicies}
          checkoutPolicies={checkoutPolicies}
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <CheckInInstructions checkinInstructions={checkinInstructions} secondaryTitleClassNames={secondaryTitleClassNames} />
        <PropertyPoliciesSection
          id="access-methods"
          model={accessMethodPolicies}
          titleLocToken="hotels.infosite.policies.accessMethods.title"
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <RenovationsAndClosures
          renovationsAndClosuresSection={renovationsAndClosuresSection}
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <PetsPolicy petsPolicy={petsPolicy} secondaryTitleClassNames={secondaryTitleClassNames} />
        <PropertyPoliciesSection
          id="child-beds"
          model={childBeds}
          titleLocToken="hotels.infosite.policies.childrenExtraBeds.title"
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <PaymentOptions paymentOptions={paymentOptions} secondaryTitleClassNames={secondaryTitleClassNames} l10n={l10n} />
      </div>
    </div>
  );
};

export const CheckInCheckOut = ({ checkinPolicies, checkoutPolicies, secondaryTitleClassNames }) => (
  <Viewport>
    <ViewSmall>
      <>
        <PropertyPoliciesSection
          id="property-policies-check-in"
          model={checkinPolicies}
          titleLocToken="hotels.infosite.policies.checkIn.title"
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <PropertyPoliciesSection
          id="property-policies-check-out"
          model={checkoutPolicies}
          titleLocToken="hotels.infosite.policies.checkOut.title"
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
      </>
    </ViewSmall>
    <ViewLarge>
      <div className="uitk-grid">
        <div className="uitk-cell all-cell-1-2">
          <PropertyPoliciesSection
            id="property-policies-check-in"
            model={checkinPolicies}
            titleLocToken="hotels.infosite.policies.checkIn.title"
            secondaryTitleClassNames={secondaryTitleClassNames}
          />
        </div>
        <div className="uitk-cell all-cell-1-2">
          <PropertyPoliciesSection
            id="property-policies-check-out"
            model={checkoutPolicies}
            titleLocToken="hotels.infosite.policies.checkOut.title"
            secondaryTitleClassNames={secondaryTitleClassNames}
          />
        </div>
      </div>
    </ViewLarge>
  </Viewport>
);

export const CheckInInstructions = ({ checkinInstructions, secondaryTitleClassNames }) => {
  if (!(checkinInstructions && checkinInstructions.lines && checkinInstructions.lines.length > 0)) {
    return null;
  }

  return (
    <div className="all-y-padding-three">
      <h3 className={secondaryTitleClassNames}>
        <LocalizedText message="hotels.infosite.policies.specialCheckInInstructions.title" />
      </h3>
      <ul className="uitk-type-list-300">
        {checkinInstructions.lines.map((text, index) => (
          <li key={index} className="uitk-type-list-300">
            {text}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const RenovationsAndClosures = ({ renovationsAndClosuresSection, secondaryTitleClassNames }) => {
  if (!(renovationsAndClosuresSection && renovationsAndClosuresSection.title && renovationsAndClosuresSection.formattedText)) {
    return null;
  }

  return (
    <div className="all-t-padding-three">
      <h3 data-stid="section-renovations-and-closures" className={secondaryTitleClassNames}>
        {renovationsAndClosuresSection.title}
      </h3>
      <div
        data-stid="renovations-and-closures-text"
        className="all-y-padding-three uitk-type-list-300"
        dangerouslySetInnerHTML={{ __html: renovationsAndClosuresSection.formattedText.description }}
      />
    </div>
  );
};

export const PetsPolicy = ({ petsPolicy, secondaryTitleClassNames }) => {
  if (!(petsPolicy && petsPolicy.title && petsPolicy.body && petsPolicy.body.length > 0)) {
    return null;
  }

  return (
    <div className="pets-policy all-y-padding-three">
      <h3 className={`pets-policy__title ${secondaryTitleClassNames}`}>{petsPolicy.title}</h3>
      <div className="all-t-padding-two">
        {petsPolicy.body.map((text, index) => renderAmenityBody(text, index, 'pets-policy__details'))}
      </div>
    </div>
  );
};

export const PaymentOptions = ({ paymentOptions, secondaryTitleClassNames, l10n }) => {
  if (!(paymentOptions && paymentOptions.length > 0)) {
    return null;
  }

  return (
    <div className="payment-types">
      <h3 className={`payment-types__title ${secondaryTitleClassNames}`}>
        <LocalizedText message="hotels.infosite.policies.paymentTypes.title" />
      </h3>
      <div className="payment-types__cards">
        {paymentOptions.map((type, index) => {
          const name = type.name;

          return (
            <LazyImage
              key={`payment-type-${name || index}`}
              experimentName="PWA_LAZY_LOAD_IMAGES"
              altPlaceholderText={name ? name : l10n.formatText('hotels.infosite.policies.paymentType.altText')}
            >
              <img src={type.imageUrl} alt={name ? name : l10n.formatText('hotels.infosite.policies.paymentType.altText')} />
            </LazyImage>
          );
        })}
      </div>
    </div>
  );
};
